// src/hooks/useMap.js
import { useEffect, useRef } from 'react';
import L from 'leaflet';

function esc(v) {
  return String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const PALETTE = [
  '#e6194b', '#3cb44b', '#ffe119', '#4363d8',
  '#f58231', '#911eb4', '#42d4f4', '#f032e6',
  '#bfef45', '#fabed4', '#469990', '#dcbeff'
];

function colorForUser(userId, userColors) {
  const keyStr = String(userId);
  if (userColors && typeof userColors === 'object') {
    if (Object.prototype.hasOwnProperty.call(userColors, keyStr)) return userColors[keyStr];
    const keyNum = Number.isNaN(Number(keyStr)) ? keyStr : Number(keyStr);
    if (Object.prototype.hasOwnProperty.call(userColors, keyNum)) return userColors[keyNum];
  }
  let h = 0;
  for (let i = 0; i < keyStr.length; i++) h = (h * 31 + keyStr.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

function validPoint(item) {
  return (
    item &&
    item.latitude != null &&
    item.longitude != null &&
    Number.isFinite(Number(item.latitude)) &&
    Number.isFinite(Number(item.longitude))
  );
}

export function useMap(containerRef, entries = [], userColors = {}, options = {}) {
  const mapRef = useRef(null);
  const hasAutoFocusedRef = useRef(false);
  const userInteractedRef = useRef(false);

  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    if (container._leaflet_id) delete container._leaflet_id;
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    hasAutoFocusedRef.current = false;
    userInteractedRef.current = false;

    const map = L.map(container, {
      zoomControl: true,
      preferCanvas: true
    }).setView([51.0504, 13.7373], 12);

    mapRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 19,
      updateWhenIdle: true,
      keepBuffer: 2
    }).addTo(map);

    map._staticLayer = L.layerGroup().addTo(map);
    map._liveLayer = L.layerGroup().addTo(map);

    const invalidate = () => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    };

    const markInteraction = () => {
      userInteractedRef.current = true;
    };

    map.on('dragstart', markInteraction);
    map.on('zoomstart', markInteraction);

    setTimeout(invalidate, 50);
    setTimeout(invalidate, 250);
    setTimeout(invalidate, 700);
    window.addEventListener('resize', invalidate);

    return () => {
      window.removeEventListener('resize', invalidate);
      if (mapRef.current) {
        mapRef.current.off('dragstart', markInteraction);
        mapRef.current.off('zoomstart', markInteraction);
        if (mapRef.current._staticLayer) {
          mapRef.current._staticLayer.clearLayers();
          delete mapRef.current._staticLayer;
        }
        if (mapRef.current._liveLayer) {
          mapRef.current._liveLayer.clearLayers();
          delete mapRef.current._liveLayer;
        }
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [containerRef]);

  useEffect(() => {
    if (!mapRef.current) return;
    if (userInteractedRef.current) return;

    const staticEntries = Array.isArray(entries) ? entries : [];
    const liveLocations = Array.isArray(options?.liveLocations) ? options.liveLocations : [];
    const allPoints = [...liveLocations, ...staticEntries].filter(validPoint);

    // Nur einmal initial sinnvoll fokussieren.
    if (hasAutoFocusedRef.current) return;

    if (allPoints.length === 0) {
      mapRef.current.setView([51.0504, 13.7373], 12);
      hasAutoFocusedRef.current = true;
      return;
    }

    if (allPoints.length === 1) {
      const p = allPoints[0];
      mapRef.current.setView([Number(p.latitude), Number(p.longitude)], 17);
      hasAutoFocusedRef.current = true;
      setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 50);
      return;
    }

    const bounds = L.latLngBounds(
      allPoints.map(p => [Number(p.latitude), Number(p.longitude)])
    );

    mapRef.current.fitBounds(bounds, {
      padding: [30, 30],
      maxZoom: 17
    });
    hasAutoFocusedRef.current = true;
    setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 50);
  }, [entries, options?.liveLocations]);

  useEffect(() => {
    if (!mapRef.current || !mapRef.current._staticLayer) return;
    const layer = mapRef.current._staticLayer;
    layer.clearLayers();

    const list = Array.isArray(entries) ? entries : [];
    for (let i = 0; i < list.length; i++) {
      const e = list[i];
      if (!validPoint(e)) continue;

      const color = colorForUser(e.user_id, userColors);
      const amount = Number(e.amount || 0).toFixed(2);
      const pname = e.product_name ? esc(e.product_name) : 'Unbekannt';
      const volume = e.volume ? ` (${esc(e.volume)})` : '';
      const barcode = e.barcode ? `<div>EAN: <code>${esc(e.barcode)}</code></div>` : '';
      const pfand = e.pfand_info ? `<div>Pfand-Info: ${esc(e.pfand_info)}</div>` : '';
      const wetter = (e.wetter_text || e.wetter_temp != null)
        ? `<div style="opacity:.8">Wetter: ${esc(e.wetter_text ?? '')}${e.wetter_temp != null ? `, ${Number(e.wetter_temp).toFixed(1)}°C` : ''}</div>`
        : '';
      const when = e.timestamp ? new Date(e.timestamp).toLocaleString() : '';

      const popupHtml = `
        <div class="pp-popup">
          <div style="font-weight:700;margin-bottom:4px;">${pname}${volume}</div>
          <div><strong>${amount} €</strong></div>
          <div style="opacity:.8">${esc(when)}</div>
          ${barcode}
          ${pfand}
          ${wetter}
        </div>
      `;

      L.circleMarker([Number(e.latitude), Number(e.longitude)], {
        radius: 8,
        color,
        fillColor: color,
        fillOpacity: 0.55,
        weight: 2
      })
        .addTo(layer)
        .bindPopup(popupHtml, { maxWidth: 280 });
    }
  }, [entries, userColors]);

  useEffect(() => {
    if (!mapRef.current || !mapRef.current._liveLayer) return;
    const layer = mapRef.current._liveLayer;
    layer.clearLayers();

    const liveLocations = Array.isArray(options?.liveLocations) ? options.liveLocations : [];

    for (const item of liveLocations) {
      if (!validPoint(item)) continue;

      const color = colorForUser(item.user_id, userColors);
      const label = esc(item.display_name || item.name || `User ${item.user_id || ''}`);
      const avatarUrl = item.avatar_url ? esc(item.avatar_url) : '';
      const when = item.timestamp ? new Date(item.timestamp).toLocaleString() : '';
      const isLocalUser = Boolean(item.is_local_user);

      let marker;
      if (isLocalUser) {
        // Exakt derselbe Marker-Stil wie in der Schatzkarte
        const localIcon = L.icon({
          iconUrl: 'https://app.pfandpirat.com/wp-content/uploads/pfandpirat.png',
          iconSize: [50, 50],
          iconAnchor: [25, 50]
        });

        marker = L.marker([Number(item.latitude), Number(item.longitude)], { icon: localIcon });
      } else {
        const html = avatarUrl
          ? `
              <div style="
                width: 28px;
                height: 28px;
                border-radius: 50%;
                overflow: hidden;
                border: 3px solid ${color};
                box-shadow: 0 0 0 2px #fff, 0 2px 8px rgba(0,0,0,.35);
                background: #fff;
              ">
                <img src="${avatarUrl}" alt="${label}" style="width:100%;height:100%;object-fit:cover;" />
              </div>
            `
          : `
              <div style="
                width: 18px;
                height: 18px;
                border-radius: 50%;
                border: 3px solid ${color};
                background: ${color};
                box-shadow: 0 0 0 2px #fff, 0 2px 8px rgba(0,0,0,.35);
              "></div>
            `;

        const icon = L.divIcon({
          className: 'pfandpirat-live-marker',
          html,
          iconSize: avatarUrl ? [28, 28] : [18, 18],
          iconAnchor: avatarUrl ? [14, 14] : [9, 9]
        });

        marker = L.marker([Number(item.latitude), Number(item.longitude)], { icon });
      }

      const popupHtml = `
        <div class="pp-popup">
          <div style="font-weight:700;margin-bottom:4px;">${label}</div>
          ${when ? `<div style="opacity:.8">${esc(when)}</div>` : ''}
        </div>
      `;

      marker.addTo(layer).bindPopup(popupHtml, { maxWidth: 240 });
    }
  }, [options?.liveLocations, userColors]);
}
